import { getApp } from '@genesislcap/foundation-shell/app';
import { html, repeat, when } from '@microsoft/fast-element';
import { buttonTag, iconTag, flyoutTag, optionTag, connectionIndicatorTag, selectTag, providerTag, } from '../tags';
const app = getApp();
const sideNavTemplate = html `
  <${flyoutTag}
    position="left"
    ?closed=${(x) => !x.sideNavOpen}
    @closed=${(x) => x.toggleNavVisibility()}
    id="side-nav"
    data-test-id="side-nav"
    role="navigation"
    aria-label="Side navigation"
  >
    <img
      slot="title"
      src=${(x) => x.logoSrc}
      class="logo"
      part="side-nav-logo"
      data-test-id="side-nav-logo"
      alt="${(x) => x.logoAltText}"
    />
    <slot name="menu-contents"></slot>
    <${buttonTag}
      slot="footer"
      class="logout-container"
      data-test-id="logout-button"
      @click=${(x) => x.logout()}
      aria-label="Sign out"
    >
      <${iconTag} name="sign-out-alt"></${iconTag}>
      Sign out
    </${buttonTag}>
  </${flyoutTag}>
`;
const defaultRouteNavItemsTemplate = html `
  <${buttonTag}
    appearance="neutral-grey"
    class="nav-button"
    @click=${(x) => x.navigateTo('protected')}
    data-test-id="home-button"
    aria-label="Home"
  >
    <${iconTag} name="home"></${iconTag}>
    Home
  </${buttonTag}>
  <${buttonTag}
    appearance="neutral-grey"
    class="nav-button"
    @click=${(x) => x.navigateTo('not-found')}
    data-test-id="profiles-button"
    aria-label="Profiles"
  >
    <${iconTag} name="users"></${iconTag}>
    Profiles
  </${buttonTag}>
`;
const routeNavItemsTemplate = html `
  <template>
    ${repeat((x) => x.routeNavItems, html `
        ${when((x, c) => !x.permission || (x.permission && c.parent.isUserPermitted(x.permission)), html `        
            <${buttonTag}
              appearance="neutral-grey"
              class="nav-button"
              slot="routes"
              @click=${(x, c) => c.parent.navigateTo(x.routePath)}
              data-test-id="${(x) => `${x.routePath}-button`}"
              aria-label=${(x) => x.title}
            >
              ${when((x) => x.icon, html `
                  <${iconTag}
                    name="${(x) => x.icon.name}"
                    variant="${(x) => x.icon.variant}"
                    size="${(x) => x.icon.size}"
                  ></${iconTag}>
                `)}
              ${(x, c) => c.parent.toLocalisedText(x.title)}
            </${buttonTag}>`)}
      `)}
  </template>
`;
/**
 * Template for Navigation class
 *
 * @remarks
 *
 * Contains the template for both the sidenav and the navigation bar
 *
 * @typeParam Navigation - Navigation class
 *
 * @returns ViewTemplate instance of the Navigation html
 *
 * @public
 */
export const NavTemplate = html `
  ${when((x) => !x.hideSideBar, sideNavTemplate)}
  <div class="nav-listbox" data-test-id="nav-bar" role="navigation" aria-label="Main menu">
    ${app.registerElementsTarget('header-start')}
    <div class="nav-leftside">
      ${app.registerElementsTarget('nav-start')}
      ${when((x) => !x.hideSideBar, html `
          <${buttonTag}
            class="bars-container"
            @click=${(x) => x.toggleNavVisibility()}
            data-test-id="hamburger-menu"
            aria-label="Toggle side menu"
            aria-controls="side-nav"
            aria-expanded=${(x) => x.sideNavOpen}
            appearance="stealth"
            @focusin=${(x) => x.closeNav()}
          >
            <${iconTag} name="bars" part="nav-visibility-icon" class="nav-visibility-icon"></${iconTag}>
          </${buttonTag}>
        `)}
      <div class="logo-container" data-test-id="nav-bar-logo">
        <img
          src=${(x) => x.logoSrc}
          class="logo"
          part="logo"
          data-test-id="logo"
          alt="${(x) => x.logoAltText}"
        />
      </div>
      <slot name="routes" data-test-id="route-buttons">
        ${(x) => (x.routeNavItems ? routeNavItemsTemplate : defaultRouteNavItemsTemplate)}
      </slot>
      ${app.registerElementsTarget('nav')}
    </div>
    <div class="nav-rightside">
      ${app.registerElementsTarget('nav-end')}
      <slot name="routes-end" data-test-id="route-end-buttons"></slot>
      ${when((x) => x.showLuminanceToggleButton, html `
          <div
            class="icon-container"
            data-test-id="luminance-toggle-button"
            part="luminance-button"
            aria-label="Luminance toggle"
          >
            <${iconTag}
              @click=${(x) => x.luminanceIconEvent()}
              variant="regular"
              name="moon"
              part="luminance-icon"
            ></${iconTag}>
          </div>
        `)}
      ${when((x) => x.showMiscToggleButton, html `
          <div class="icon-container" data-test-id="misc-toggle-button" part="misc-button" aria-label="Misc icon">
            <${iconTag} @click=${(x) => x.miscIconEvent()} name="th" part="misc-icon"></${iconTag}>
          </div>
        `)}
      ${when((x) => x.showConnectionIndicator, html `
          <div class="connection-indicator-container" data-test-id="connection-indicator">
            <${connectionIndicatorTag} show-label="false"></${connectionIndicatorTag}>
          </div>
        `)}
      ${when((x) => x.showLanguageSelector && x.languageOptions, html `
          <${selectTag}
            class="language-selector"
            position="below"
            data-test-id="language-selector"
            part="language-selector"
            @change="${(x, c) => x.changeLanguage(c.event)}"
            aria-label="Language selector"
          >
            ${repeat((x) => x.languageOptions.availableLanguages, html `
                <${optionTag}
                  value="${(x) => x}"
                  ?selected="${(x, c) => x === c.parent.languageOptions.selectedLanguage}"
                >
                  ${(x) => x.toUpperCase()}
                </${optionTag}>
              `)}
          </${selectTag}>
        `)}

      <${buttonTag} appearance="neutral-grey" data-test-id="user-button" class="nav-button" aria-label="User button">
        <${iconTag} name="user-circle"></${iconTag}>
        ${(x) => x.userName}
      </${buttonTag}>
    </div>
    ${app.registerElementsTarget(['header', 'header-end'])}
  </div>
`;
/**
 * Top level template for Navigation class
 *
 * @remarks
 *
 * Dynamic template which wraps the code in a provider
 *
 * @typeParam Navigation - Navigation class
 *
 * @returns ViewTemplate instance of the Navigation html
 *
 * @internal
 */
export const DynamicTemplate = html `
  <${providerTag} data-test-id="header-provider">
    <div class="dynamic-template">${(x) => x.selectTemplate()}</div>
  </${providerTag}>
`;
/**
 * Loading template for the Navigation class
 *
 * @remarks
 *
 * Template used to indicate loading to the user
 *
 * @typeParam Navigation - Navigation class
 *
 * @returns ViewTemplate instance of the Loading html
 *
 * @internal
 */
export const LoadingTemplate = html `
  ...
`;
/**
 * Render template for the Navigation class
 *
 * @remarks
 *
 * Template used when the class has finished loading
 *
 * @typeParam Navigation - Navigation class
 *
 * @returns ViewTemplate instance of the Main html
 *
 * @internal
 */
export const MainTemplate = html `
  ${NavTemplate}
`;
